var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useEffect, forwardRef, useImperativeHandle, createElement } from '@max/max';
import View from '@hfe/max-view';
import Image from '@hfe/max-image';
import { Animated } from '@max/mbc-animated';
import AnimatedView from '@max/mbc-animate-view';
import { rpx2px } from '@hfe/max-universal-unit-tool';
import './index.scss';
import { getSystemType, SystemType } from '@nibfe/mkt-env-api';
const isInHarmony = getSystemType() === (SystemType === null || SystemType === void 0 ? void 0 : SystemType.harmony);
// 常量定义
const PRIZE_HEIGHT = 160;
const PRIZE_OFFSET = 12;
const REEL_WIDTH = 140;
const REPEAT_COUNT = 8; // 重复渲染次数，确保无限滚动
const SPIN_TIME = 2200; // 抽奖滚动时间2秒
const BOUNCE_TIME = 160; // 回弹时间 160ms
const BOUNCE_OFFSET = 6; // 回弹偏移量 6px
const CENTER_OFFSET = -6;
const INIT_POSITION = rpx2px(-848);
const SlotMachine = forwardRef((props, ref) => {
    const { reelImage = [], prizeCount = 13, winningIndexes = [], initialIndexes = [], onStart, onEnd, onReset, slotMachineSpinController, style } = props;
    // 根据奖品索引计算静止时的初始偏移量（不含滚动圈数）
    const calcInitialPosition = (reelIndex) => {
        const prizeHeight = rpx2px(PRIZE_HEIGHT);
        const prizeOffset = rpx2px(PRIZE_OFFSET);
        const idx = initialIndexes[reelIndex];
        if (idx === undefined || idx < 0) {
            return INIT_POSITION;
        }
        return -(idx * (prizeHeight + prizeOffset) + CENTER_OFFSET);
    };
    // 动画值（初始位置由 initialIndexes 决定，无则使用 INIT_POSITION）
    const animatedPosY1 = useRef(new Animated.Value(calcInitialPosition(0))).current;
    const animatedPosY2 = useRef(new Animated.Value(calcInitialPosition(1))).current;
    const animatedPosY3 = useRef(new Animated.Value(calcInitialPosition(2))).current;
    // 暴露 resetSpin 方法给父组件
    useImperativeHandle(ref, () => ({
        resetSpin: () => {
            onReset && onReset();
            animatedPosY1.setValue(calcInitialPosition(0));
            animatedPosY2.setValue(calcInitialPosition(1));
            animatedPosY3.setValue(calcInitialPosition(2));
        }
    }), [onReset]);
    // 监听抽奖控制器
    useEffect(() => {
        if (slotMachineSpinController > 0) {
            startSpin();
        }
    }, [slotMachineSpinController]);
    // 计算终点位置，确保中奖奖品显示在中心
    const calculateEndPosition = (reelIndex) => {
        const cycles = 3;
        const prizeHeight = rpx2px(PRIZE_HEIGHT);
        const prizeOffset = rpx2px(PRIZE_OFFSET);
        const winningIndex = winningIndexes[reelIndex];
        const singleReelHeight = prizeHeight * prizeCount + prizeOffset * prizeCount;
        if (winningIndex === undefined || !winningIndexes.length || winningIndex < 0) {
            return singleReelHeight * cycles + CENTER_OFFSET;
        }
        const targetPrizePosition = winningIndex * (prizeHeight + prizeOffset);
        const endPosition = singleReelHeight * cycles + targetPrizePosition + CENTER_OFFSET;
        return endPosition;
    };
    // 创建速率曲线
    const createCustomSpeedCurve = () => {
        return (t) => {
            if (t <= 0.098) {
                const progress = t / 0.098;
                return progress * 0.15;
            }
            else if (t <= 0.49) {
                const progress = (t - 0.098) / 0.392;
                return 0.15 + 0.35 * Math.pow(progress, 0.6);
            }
            else if (t <= 0.83) {
                const progress = (t - 0.49) / 0.34;
                return 0.5 + 0.25 * Math.pow(progress, 1.2);
            }
            else if (t <= 0.98) {
                const progress = (t - 0.83) / 0.15;
                return 0.75 + 0.15 * Math.pow(progress, 1.8);
            }
            else {
                const progress = (t - 0.98) / 0.02;
                return 0.9 + 0.1 * progress;
            }
        };
    };
    // 快速滚动
    const startSpin = () => __awaiter(void 0, void 0, void 0, function* () {
        onStart && onStart();
        const animateReelWithCustomCurve = (animatedValue, reelIndex, delay) => {
            const endPosition = calculateEndPosition(reelIndex);
            const bouncePosition = endPosition + BOUNCE_OFFSET;
            setTimeout(() => {
                Animated.timing(animatedValue, {
                    toValue: -bouncePosition,
                    duration: SPIN_TIME - BOUNCE_TIME,
                    easing: createCustomSpeedCurve(),
                    useNativeDriver: true
                }).start(() => {
                    Animated.timing(animatedValue, {
                        toValue: -endPosition,
                        duration: BOUNCE_TIME,
                        easing: Animated.Easing.out(Animated.Easing.quad),
                        useNativeDriver: true
                    }).start(() => {
                        // 动画完成
                    });
                });
            }, delay);
        };
        // 从个位数开始，间隔320ms依次启动动画
        animateReelWithCustomCurve(animatedPosY3, 2, 640);
        animateReelWithCustomCurve(animatedPosY2, 1, 320);
        animateReelWithCustomCurve(animatedPosY1, 0, 0);
        const totalAnimationTime = 1280 + SPIN_TIME;
        setTimeout(() => {
            onEnd && onEnd();
        }, totalAnimationTime);
    });
    // 渲染单个轮子
    const renderReel = (animatedValue, reelIndex) => {
        const totalImageHeight = PRIZE_HEIGHT * prizeCount + PRIZE_OFFSET * prizeCount;
        return <View key={`reel-${reelIndex}`} className="slot-machine__reel">
        <AnimatedView viewId='' style={{
            width: `${REEL_WIDTH}rpx`,
            height: `${totalImageHeight * REPEAT_COUNT}rpx`,
            transform: [{
                    translateY: animatedValue
                }],
            marginTop: isInHarmony ? '2rpx' : '0rpx'
        }}>
          {Array.from({
            length: REPEAT_COUNT
        }, (_, index) => <Image key={`image-${index}`} source={{
            uri: reelImage[reelIndex]
        }} style={{
            width: `${REEL_WIDTH}rpx`,
            height: `${totalImageHeight}rpx`
        }} resizeMode="cover"/>)}
        </AnimatedView>
      </View>;
    };
    return <View className="slot-machine__container" style={style}>
      <View className="slot-machine__reels-container">
        {renderReel(animatedPosY1, 0)}
        {renderReel(animatedPosY2, 1)}
        {renderReel(animatedPosY3, 2)}
      </View>
    </View>;
});
export default SlotMachine;
